import React from 'react';
import { Icon } from '@plone/volto/components';
import menuSVG from '@plone/volto/icons/menu.svg';
import closeSVG from '@plone/volto/icons/clear.svg';

export interface MobileSidebarSheetProps {
  /** Conteúdo da sidebar (cartões de slot, "Links úteis" etc.). */
  children: React.ReactNode;
  /** Rótulo do botão de abrir e do painel — ex.: "Menu lateral", "Links úteis". */
  label: string;
  /**
   * Identidade da página (ex.: `@id` do conteúdo). Quando muda com o painel
   * aberto, o conteúdo novo chegou: o painel desce e fecha, revelando-o.
   */
  closeKey?: string;
}

// Mesma duração da transição de `transform` em `_mobile-sidebar-sheet.scss`.
const CLOSE_ANIMATION_MS = 250;

/**
 * RNF01: em mobile a sidebar vira um bottom sheet — escondida por padrão,
 * atrás de um botão, em vez de empilhada abaixo do conteúdo o tempo todo.
 * Em telas `lg` (1024px) para cima, o comportamento de sempre continua
 * (conteúdo sempre visível, sem botão, sem diálogo). Até 1023px vale o
 * painel: em tablet a coluna lateral cortava os rótulos (AB#11383).
 *
 * Usa `<dialog>` nativo no mesmo espírito do `YoutubeDialog.tsx`: o elemento
 * nasce sempre no DOM (fechado, já oculto pelo navegador), aberto via
 * `showModal()` — foco, Esc-to-close e trava de scroll de fundo vêm de
 * graça, sem reimplementar nada disso à mão. Em telas `lg+`, um override de
 * CSS força `display: block` independente do estado `open`, fazendo o mesmo
 * elemento renderizar como bloco normal do fluxo — não precisa de nenhuma
 * lógica de media query em JS nem duplica o conteúdo no DOM. O override
 * de CSS vale de `lg` para cima.
 */
export const MobileSidebarSheet: React.FC<MobileSidebarSheetProps> = ({
  children,
  label,
  closeKey,
}) => {
  const ref = React.useRef<HTMLDialogElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [dragOffset, setDragOffset] = React.useState(0);
  const [isClosing, setIsClosing] = React.useState(false);
  const dragStartY = React.useRef<number | null>(null);

  const open = React.useCallback(() => {
    const dialog = ref.current;
    if (dialog && typeof dialog.showModal === 'function' && !dialog.open) {
      dialog.showModal();
      setIsOpen(true);
    }
  }, []);

  // Atualiza o estado do React na hora, sem depender só do evento `close`
  // nativo disparar (o listener abaixo continua existindo para o caso do
  // Esc, que fecha o dialog sem passar por esta função).
  const close = React.useCallback(() => {
    const dialog = ref.current;
    if (dialog && dialog.open) {
      dialog.close();
      setIsOpen(false);
      setDragOffset(0);
    }
  }, []);

  // Navegar por um link do painel não recarrega a página (SSR + roteamento
  // no navegador), então nada fechava o painel e o visitante não via que o
  // conteúdo tinha chegado. Quando a página muda, o painel desce suave e
  // fecha; com movimento reduzido, fecha direto.
  const previousCloseKey = React.useRef(closeKey);
  React.useEffect(() => {
    if (previousCloseKey.current === closeKey) return undefined;
    previousCloseKey.current = closeKey;
    if (!ref.current?.open) return undefined;
    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      close();
      return undefined;
    }
    setDragOffset(0);
    setIsClosing(true);
    const timer = window.setTimeout(() => {
      setIsClosing(false);
      close();
    }, CLOSE_ANIMATION_MS);
    return () => {
      window.clearTimeout(timer);
      setIsClosing(false);
    };
  }, [closeKey, close]);

  // Only Esc passa por aqui de fato (fecha o dialog nativamente, sem chamar
  // `close()` acima) — mantido como rede de segurança para esse caminho.
  React.useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return undefined;
    const handleClose = () => {
      setIsOpen(false);
      setDragOffset(0);
    };
    dialog.addEventListener('close', handleClose);
    return () => dialog.removeEventListener('close', handleClose);
  }, []);

  // Clique no fundo. Ouvinte imperativo (não `onClick` no JSX): o alvo é o
  // próprio `<dialog>` — é assim que o clique no backdrop se comporta.
  React.useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return undefined;
    const handleBackdropClick = (event: MouseEvent) => {
      if (event.target === dialog) close();
    };
    dialog.addEventListener('click', handleBackdropClick);
    return () => dialog.removeEventListener('click', handleBackdropClick);
  }, [close]);

  // Arrastar para fechar: só a alça no topo do painel responde ao toque, para
  // não capturar gestos de rolagem do conteúdo. Arrastar mais de 25% da
  // altura do painel fecha; menos que isso, o painel volta pro lugar (a
  // transição de CSS cuida do "encaixe" porque só removemos o `dragOffset`).
  const handleTouchStart = (event: React.TouchEvent) => {
    dragStartY.current = event.touches[0].clientY;
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    if (dragStartY.current === null) return;
    const delta = event.touches[0].clientY - dragStartY.current;
    if (delta > 0) setDragOffset(delta);
  };

  const handleTouchEnd = () => {
    if (dragStartY.current === null) return;
    const dialog = ref.current;
    const threshold = (dialog?.clientHeight || 300) * 0.25;
    if (dragOffset > threshold) {
      close();
    } else {
      setDragOffset(0);
    }
    dragStartY.current = null;
  };

  return (
    <div className="mpf-mobile-sidebar-sheet">
      <button
        type="button"
        className="mpf-mobile-sidebar-sheet__trigger"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={open}
      >
        <Icon name={menuSVG} size="20px" />
        {label}
      </button>

      <dialog
        ref={ref}
        aria-label={label}
        className={`mpf-mobile-sidebar-sheet__dialog${
          isClosing ? ' mpf-mobile-sidebar-sheet__dialog--closing' : ''
        }`}
        onClose={() => setIsOpen(false)}
        style={
          dragOffset
            ? { transform: `translateY(${dragOffset}px)`, transition: 'none' }
            : undefined
        }
      >
        <div
          className="mpf-mobile-sidebar-sheet__handle"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <span
            className="mpf-mobile-sidebar-sheet__handle-bar"
            aria-hidden="true"
          />
          <button
            type="button"
            className="mpf-mobile-sidebar-sheet__close"
            aria-label="Fechar"
            onClick={close}
          >
            <Icon name={closeSVG} size="16px" />
          </button>
        </div>
        <div className="mpf-mobile-sidebar-sheet__content">{children}</div>
      </dialog>
    </div>
  );
};

export default MobileSidebarSheet;
